export enum Environment {
    Development = 'development',
    Staging = 'staging',
    Production = 'production',
}

const STAGING_HOSTNAME = 'web.optoscale.dev';
const DEVELOPMENT_HOSTNAMES = ['localhost', '127.0.0.1'];
// Per-PR ephemeral environments (UAT previews and frontend previews).
// These must never report to analytics/telemetry services — see
// isEphemeralHostname() consumers in initializeAnalyticsTools.ts and Root.tsx.
const EPHEMERAL_HOSTNAME_RE = /^(uat|preview)-pr-\d+\.optoscale\.dev$/;

/**
 * True when running on a per-PR ephemeral environment
 * (uat-pr-N.optoscale.dev / preview-pr-N.optoscale.dev). Ephemeral traffic
 * is test traffic: it must not pollute Sentry, PostHog, GA or Hotjar.
 */
export function isEphemeralHostname(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    return EPHEMERAL_HOSTNAME_RE.test(window.location.hostname);
}

/**
 * Detects the current environment based on the browser's hostname.
 * This is useful for runtime environment detection since the build process
 * always sets NODE_ENV='production' for both staging and production builds.
 *
 * @returns The detected environment:
 * - 'development' for localhost/127.0.0.1
 * - 'staging' for web.optoscale.dev
 * - 'production' for web.optoscale.no or any other domain
 */
export function getEnvironmentByHostname(): Environment {
    if (typeof window === 'undefined') {
        return Environment.Production;
    }

    const hostname = window.location.hostname;

    // Development (local)
    if (DEVELOPMENT_HOSTNAMES.includes(hostname)) {
        return Environment.Development;
    }

    // Per-PR ephemeral environments — previously fell through to Production,
    // which sent UAT/preview test sessions to production Sentry/PostHog/GA/
    // Hotjar. Development gives them the all-off analytics config.
    if (isEphemeralHostname()) {
        return Environment.Development;
    }

    // Staging
    if (hostname === STAGING_HOSTNAME) {
        return Environment.Staging;
    }

    // Production (web.optoscale.no) and any other domain
    return Environment.Production;
}

/**
 * Checks if the app is running in production environment
 */
export function isProduction(): boolean {
    return getEnvironmentByHostname() === Environment.Production;
}

/**
 * Checks if the app is running in staging environment
 */
export function isStaging(): boolean {
    return getEnvironmentByHostname() === Environment.Staging;
}

/**
 * Checks if the app is running in development environment (localhost)
 */
export function isDevelopment(): boolean {
    return getEnvironmentByHostname() === Environment.Development;
}

/**
 * Checks if the app is running in a non-production environment.
 * Returns true for both development (localhost) and staging (web.optoscale.dev).
 * This is particularly useful for enabling features like local feature flags.
 */
export function isNonProduction(): boolean {
    return !isProduction();
}
